/**
 * ExperienceSettings → Explorer 内部类型的映射器。
 *
 * 运镜：animTracks 透传为 Track（结构同源）；无 animTracks 时用 cameras
 * 生成等间距轨迹。annotations → Hotspot[]。cameras[0] → initialCamera。
 */
import type { ExperienceSettings } from '@app-types/experience';
import { Vec3, type Track } from '@mindcloudxai_3d/core';
import type { Hotspot, SceneCameraPreset } from '@app-types/scene';
import { createLogger } from '@utils/logger';

const log = createLogger('experienceMapper');

// ---------------------------------------------------------------------------
// Public mapper output
// ---------------------------------------------------------------------------

export interface ExperienceMapped {
  initialCamera: SceneCameraPreset | null;
  track: Track | null;
  hotspots: Hotspot[];
  startMode: ExperienceSettings['startMode'];
}

// ---------------------------------------------------------------------------
// Main mapper
// ---------------------------------------------------------------------------

export function mapExperienceSettings(settings: ExperienceSettings): ExperienceMapped {
  const track = mapTrack(settings);
  const hotspots = mapAnnotations(settings);
  // 有 track 时用轨迹起点作为初始相机，确保开场位置与运镜起点一致
  const initialCamera = (track && initialCameraFromTrack(track)) ?? mapInitialCamera(settings);

  return {
    initialCamera,
    track,
    hotspots,
    startMode: settings.startMode ?? 'default',
  };
}

// ---------------------------------------------------------------------------
// Initial camera
// ---------------------------------------------------------------------------

function mapInitialCamera(settings: ExperienceSettings): SceneCameraPreset | null {
  const cam = settings.cameras?.[0];
  if (!cam?.initial) return null;
  return {
    position: toVec3(cam.initial.position),
    target: toVec3(cam.initial.target),
    fov: cam.initial.fov,
  };
}

// ---------------------------------------------------------------------------
// Track: animTracks 优先，否则用 cameras 生成静态轨迹
// ---------------------------------------------------------------------------

function mapTrack(settings: ExperienceSettings): Track | null {
  if (settings.animTracks?.length > 0) {
    return mapAnimTrack(settings.animTracks[0]);
  }
  // 无 animTracks 时，用 cameras 数组作为单帧/多帧轨迹
  if (settings.cameras?.length > 0) {
    return mapCamerasAsTrack(settings.cameras);
  }
  return null;
}

/** animTrack 与 Track 结构同源；将 supersplat 帧制 times 转为秒后透传。 */
function mapAnimTrack(anim: ExperienceSettings['animTracks'][number]): Track | null {
  const times = anim.keyframes?.times;
  const values = anim.keyframes?.values;
  if (!times || !values || times.length === 0) {
    log.warn('mapAnimTrack: empty keyframes', anim.name);
    return null;
  }

  const expectedLen = times.length * 3;
  if (values.position.length < expectedLen || values.target.length < expectedLen) {
    log.warn('mapAnimTrack: position/target 数组长度不足，可能产生零值关键帧', {
      name: anim.name,
      times: times.length,
      posLen: values.position.length,
      targetLen: values.target.length,
    });
  }

  // supersplat v2 schema 中 times 单位为帧（秒 × frameRate），Track 契约统一为秒
  const frameRate = anim.frameRate > 0 ? anim.frameRate : 30;
  const timesInSeconds = times.map((t) => t / frameRate);

  return {
    name: anim.name,
    duration: anim.duration,
    loopMode: anim.loopMode,
    interpolation: anim.interpolation,
    smoothness: anim.smoothness,
    keyframes: {
      times: timesInSeconds,
      values: anim.keyframes.values,
    },
  };
}

// ---------------------------------------------------------------------------
// Track → initial camera
// ---------------------------------------------------------------------------

/** 从 Track 第一帧提取初始相机位姿，用于覆盖 cameras[0].initial */
export function initialCameraFromTrack(track: Track): SceneCameraPreset | null {
  const { position, target, fov } = track.keyframes.values;
  if (position.length < 3 || target.length < 3) return null;
  return {
    position: new Vec3(position[0], position[1], position[2]),
    target: new Vec3(target[0], target[1], target[2]),
    fov: fov?.[0],
  };
}

/** 将 cameras 数组转为等间距轨迹（默认每帧 5 秒，times 单位为秒） */
function mapCamerasAsTrack(cameras: ExperienceSettings['cameras']): Track | null {
  if (cameras.length === 0) return null;

  const durationPerCamera = 5;
  const times: number[] = [];
  const position: number[] = [];
  const target: number[] = [];
  const fov: number[] = [];

  cameras.forEach((cam, i) => {
    times.push(i * durationPerCamera);
    position.push(...cam.initial.position);
    target.push(...cam.initial.target);
    fov.push(cam.initial.fov || 60);
  });

  return {
    name: 'cameras',
    duration: cameras.length * durationPerCamera,
    loopMode: 'repeat',
    interpolation: 'spline',
    keyframes: { times, values: { position, target, fov } },
  };
}

// ---------------------------------------------------------------------------
// Annotations → Hotspots
// ---------------------------------------------------------------------------

function mapAnnotations(settings: ExperienceSettings): Hotspot[] {
  if (!settings.annotations?.length) return [];
  return settings.annotations.map((ann, i) => ({
    id: `annotation-${i}`,
    position: toVec3(ann.position),
    lookAt: ann.camera?.initial ? toVec3(ann.camera.initial.target) : undefined,
    title: ann.title || `Annotation ${i + 1}`,
    description: ann.text || undefined,
  }));
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toVec3(arr: [number, number, number] | number[]): Vec3 {
  return new Vec3(arr[0] ?? 0, arr[1] ?? 0, arr[2] ?? 0);
}
